import { Wrapper } from "@googlemaps/react-wrapper"
import { renderMap } from "app/brands/components/Map"
import { decodeWith } from "app/common/core/decode"
import { justThrow } from "app/common/fp-ts"
import { logException } from "app/common/logging"
import { optionalRender } from "app/common/ui/optionalRender"
import { array, either, number, option, ord } from "fp-ts"
import { flow, pipe } from "fp-ts/function"
import * as t from "io-ts"
import { Optional } from "monocle-ts"
import { FC, useEffect, useRef, useState } from "react"
import { tw } from "twind"

type PlacePhoto = google.maps.places.PlacePhoto

interface InnerProps {
  placeId: string
}

const Inner: FC<InnerProps> = ({ placeId }) => {
  const ref = useRef<HTMLDivElement>(null)

  const [place, setPlace] = useState<google.maps.places.PlaceResult | undefined>(undefined)

  function callback(
    place: google.maps.places.PlaceResult | null,
    status: google.maps.places.PlacesServiceStatus
  ) {
    if (status == window.google.maps.places.PlacesServiceStatus.OK && place) {
      setPlace(place)
    }
  }

  useEffect(() => {
    if (ref.current) {
      const request: google.maps.places.PlaceDetailsRequest = {
        fields: ["photos"],
        placeId,
      }

      const serviceRef = new window.google.maps.places.PlacesService(ref.current)
      serviceRef.getDetails(request, callback)
    }
  }, [ref, placeId])

  return (
    <div ref={ref}>
      {pipe(
        place,
        option.fromNullable,
        option.chain(Optional.fromPath<google.maps.places.PlaceResult>()(["photos"]).getOption),
        option.map(
          flow(
            array.sort(
              ord.reverse(
                ord.contramap<number, PlacePhoto>((photo) => photo.width / photo.height)(number.Ord)
              )
            ),
            array.map((photo) => {
              const url = photo.getUrl()

              return (
                <li key={url}>
                  <img alt="" height={photo.height} src={url} width={photo.width} />
                  {photo.html_attributions &&
                    pipe(
                      photo.html_attributions,
                      array.map((attribution) => (
                        <li
                          className={tw`text(xs gray-400 right) block`}
                          dangerouslySetInnerHTML={{ __html: attribution }}
                          key={attribution}
                        />
                      )),
                      (lis) => <ul>{lis}</ul>
                    )}
                </li>
              )
            })
          )
        ),
        optionalRender((lis) => <ul className={tw(`ram(xs sm:sm)`)}>{lis}</ul>)
      )}
    </div>
  )
}

interface GooglePlacePhotosProps {
  placeId: string
}

export const GooglePlacePhotos: FC<GooglePlacePhotosProps> = ({ placeId }) => {
  const apiKey = pipe(
    process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
    decodeWith(t.string),
    either.mapLeft(logException),
    either.getOrElseW(justThrow)
  )

  return (
    <Wrapper apiKey={apiKey} libraries={["places"]} render={renderMap}>
      <Inner placeId={placeId} />
    </Wrapper>
  )
}
